/**
 * Thin client for the AIStyle backend.
 *
 * Auth is a httpOnly session cookie, so there is no token to store or attach —
 * `credentials: "include"` is what signs every request. In development Vite
 * proxies /api to the API server (see vite.config.ts), which keeps the cookie
 * same-origin; in production set VITE_API_URL to the API's public origin.
 */

const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ?? "";

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

type FetchOptions = NonNullable<Parameters<typeof fetch>[1]>;

async function send(path: string, init: FetchOptions = {}): Promise<Response> {
  try {
    return await fetch(`${BASE}/api${path}`, {
      ...init,
      credentials: "include",
      headers: {
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError(0, "Could not reach the server. Please check your connection.");
  }
}

function errorFrom(response: Response, data: unknown): ApiError {
  const message =
    data && typeof data === "object" && "error" in data
      ? String((data as { error: unknown }).error)
      : `Request failed (${response.status})`;
  return new ApiError(response.status, message);
}

async function request<T>(path: string, init: FetchOptions = {}): Promise<T> {
  const response = await send(path, init);
  const text = await response.text();
  const data = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) throw errorFrom(response, data);

  return data as T;
}

/** Binary download (PDFs) plus the filename the server chose for it. */
async function file(path: string): Promise<{ blob: Blob; filename: string | undefined }> {
  const response = await send(path);
  if (!response.ok) {
    let data: unknown = null;
    try {
      data = await response.json();
    } catch {
      // Not JSON — keep the generic message.
    }
    throw errorFrom(response, data);
  }
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1];
  return { blob: await response.blob(), filename };
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  file,
};

// ── Shared response shapes ──────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  /** Only "admin" may create gateway API keys and webhooks. */
  role: "user" | "admin";
}

export interface Profile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  plan: string;
  credits: number;
  created_at?: string;
  updated_at?: string;
}

export interface SessionResponse {
  user: AuthUser | null;
  profile: Profile | null;
}

/** Points an account already held from purchases made before signing up. */
export interface PurchaseBonus {
  points: number;
  purchases: number;
}

/** /auth/signup/verify: the new session, plus any points the account already held. */
export interface SignupVerified extends SessionResponse {
  bonus?: PurchaseBonus | null;
}

/** /auth/signup and /auth/signup/resend: a code was emailed to `email`. */
export interface SignupPending {
  verificationRequired: true;
  email: string;
  expiresInSeconds: number;
  resendAfterSeconds: number;
}

export interface GenerationResponse {
  imageUrl?: string;
  videoUrl?: string;
  provider?: string;
  model?: string;
  mimeType?: string;
  fallback?: boolean;
  duration?: number;
  /** Remaining balance after the charge — null for signed-out visitors. */
  credits: number | null;
}

export interface VerifyResponse {
  paid: boolean;
  pendingCapture?: boolean;
  /** PostBank has not reached a final answer yet. */
  pending?: boolean;
  /** PostBank sync: where the order stands after the check. */
  status?: "paid" | "cancelled" | "open";
  /** PostBank holds no transaction for this order — the card form was never sent. */
  notFound?: boolean;
  alreadyProcessed?: boolean;
  /** DSK order status; PostBank reports `actionCode` instead. */
  orderStatus?: number;
  actionCode?: string;
  actionCodeDescription?: string;
  creditsAdded?: number;
  profile?: { plan: string; credits: number };
}

// ── Order history ───────────────────────────────────────────────────────────

export type OrderStatus = "paid" | "refunded" | "pending_payment" | "cancelled";

/** One credit purchase in the signed-in customer's history. */
export interface OrderSummary {
  id: number;
  status: OrderStatus;
  planName: string;
  description: string | null;
  /** Credits the order grants (or granted) once paid. */
  credits: number | null;
  amount: number;
  currency: string;
  /** The bank that took the card, once one was chosen. */
  provider: "dsk" | "postbank" | null;
  createdAt: string;
  paidAt: string | null;
  receiptNumber: string | null;
}

export interface OrderList {
  orders: OrderSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface OrderDetail extends OrderSummary {
  level: string | null;
  quantity: number;
  /** Before any promo code; `amount` is what was paid. */
  unitPrice: number;
  /** The promo code used at checkout, if any. */
  discount: { code: string; amount: number } | null;
  /** The bank's own reference for the payment. */
  reference: string | null;
  updatedAt: string;
  billing: {
    name: string | null;
    email: string | null;
    phone: string | null;
    company: string | null;
    address: {
      line1: string;
      line2: string;
      city: string;
      postal_code: string;
      country: string;
    } | null;
  };
}

export type OrderDocument = "receipt" | "invoice";

/** Opens a paid order's receipt or invoice PDF in a new tab. */
export async function openOrderDocument(orderId: number, kind: OrderDocument): Promise<void> {
  // Opened before the await, so the popup blocker still sees the click.
  const tab = window.open("", "_blank");
  try {
    const { blob } = await api.file(`/orders/${orderId}/${kind}`);
    const url = URL.createObjectURL(blob);
    if (tab) tab.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    tab?.close();
    throw err;
  }
}

/**
 * Saves a paid order's receipt or invoice PDF under the server's filename.
 * With `ref` (the order's pay id) it works signed out, as on the payment result page.
 */
export async function downloadOrderDocument(orderId: number, kind: OrderDocument, ref?: string): Promise<void> {
  const path = ref
    ? `/payments/orders/${orderId}/${kind}?ref=${encodeURIComponent(ref)}&download=1`
    : `/orders/${orderId}/${kind}?download=1`;
  const { blob, filename } = await api.file(path);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename ?? `${kind}-${orderId}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser a moment to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
